import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ScrollText, User, CalendarDays } from "lucide-react";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const ProgrammerAudit = () => {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/programmer/audit-logs`, {
        withCredentials: true,
        params: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(action !== "all" ? { action } : {}),
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
        },
      });
      setLogs(response.data.logs || []);
      setActions(response.data.actions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar auditoria");
    } finally {
      setLoading(false);
    }
  }, [search, action, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="font-serif text-3xl text-navy">Auditoria do Sistema</h1>
          <p className="text-slate-600">Acompanhe o histórico de ações com descrição simples, usuário e data.</p>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input placeholder="Buscar por usuário, e-mail ou descrição" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger>
                  <SelectValue placeholder="Ação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as ações</SelectItem>
                  {actions.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              <Button onClick={fetchLogs} className="bg-navy hover:bg-navy/90">Aplicar filtros</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Eventos registrados</CardTitle>
            <CardDescription>Total de logs no filtro atual: {logs.length}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ScrollText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                Nenhum log encontrado para esse filtro.
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.log_id} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="bg-navy text-white">{log.action}</Badge>
                          {log.entity_type ? <Badge variant="outline">{log.entity_type}</Badge> : null}
                        </div>
                        <p className="text-sm font-medium text-navy">{log.description}</p>
                        <div className="flex flex-col gap-1 text-xs text-slate-500 md:flex-row md:items-center md:gap-4">
                          <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{log.user_name || "Sistema"} {log.user_email ? `(${log.user_email})` : ""}</span>
                          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{formatDate(log.created_at)}</span>
                        </div>
                      </div>
                      {log.entity_id ? <p className="text-xs text-slate-400">ID: {log.entity_id}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ProgrammerAudit;
